/**
 * Tab hide/resume for Matrix sync.
 *
 * `/sync` sends `store.getSyncToken()` as `since`. After a long gap the SDK CATCHUPs
 * every missed event. Frozen mobile tabs often skip `visibilitychange:hidden`, so we
 * also persist last PREPARED/SYNCING and use that to decide on a limited initial /sync.
 */

import {
  MATRIX_LAST_SUCCESSFUL_SYNC_AT_KEY,
  MATRIX_RESUME_FRESH_SYNC_AFTER_HIDDEN_MS,
  MATRIX_TAB_HIDDEN_AT_KEY,
  TAB_VISIBLE_FORCE_RECOVERY_AFTER_HIDDEN_MS,
} from './matrixConstants';

let hiddenAt = null;
let lastSyncAt = null;
let lastSyncSavedAt = 0;
const SAVE_SYNC_AT_EVERY_MS = 5000;

/** @type {Set<(payload: MatrixTabResumePayload) => void>} */
const resumeListeners = new Set();
let coordinatorAttached = false;
let resumeConsumedForCycle = false;

/**
 * @typedef {Object} MatrixTabResumePayload
 * @property {number} hiddenMs
 * @property {boolean} resumeFreshInitialSync
 * @property {boolean} longIdle
 * @property {string} source
 */

function writeStorage(key, value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, String(value));
  } catch (_) {
    // private mode / quota
  }
}

function readStorage(key, { remove = false } = {}) {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (remove) window.localStorage.removeItem(key);
    if (raw == null) return null;
    const n = parseInt(String(raw), 10);
    return Number.isFinite(n) ? n : null;
  } catch (_) {
    return null;
  }
}

function recordTabHidden() {
  resumeConsumedForCycle = false;
  hiddenAt = Date.now();
  writeStorage(MATRIX_TAB_HIDDEN_AT_KEY, hiddenAt);
}

function getLastSyncAt() {
  if (lastSyncAt != null) return lastSyncAt;
  const stored = readStorage(MATRIX_LAST_SUCCESSFUL_SYNC_AT_KEY);
  if (stored != null) lastSyncAt = stored;
  return lastSyncAt || 0;
}

/** Call on PREPARED/SYNCING only — not CATCHUP. */
export function markMatrixSyncSuccess(at = Date.now()) {
  if (!Number.isFinite(at) || at <= 0) return;
  lastSyncAt = at;
  if (at - lastSyncSavedAt < SAVE_SYNC_AT_EVERY_MS) return;
  lastSyncSavedAt = at;
  writeStorage(MATRIX_LAST_SUCCESSFUL_SYNC_AT_KEY, at);
}

/** True when `since` is old enough that /sync would CATCHUP for a long time. */
export function isMatrixSyncStale(now = Date.now()) {
  const last = getLastSyncAt();
  return last > 0 && now - last >= MATRIX_RESUME_FRESH_SYNC_AFTER_HIDDEN_MS;
}

function needsFreshInitialSync(hiddenMs = 0) {
  if (isMatrixSyncStale()) return true;
  // No clock yet (never PREPARED, or hide events missed): use tab-hide time.
  if (!getLastSyncAt()) return hiddenMs >= MATRIX_RESUME_FRESH_SYNC_AFTER_HIDDEN_MS;
  return false;
}

export function clearMatrixSyncClock() {
  lastSyncAt = null;
  lastSyncSavedAt = 0;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(MATRIX_LAST_SUCCESSFUL_SYNC_AT_KEY);
  } catch (_) {
    // ignore
  }
}

/** Cold boot already runs startClient with no `since`. */
export function clearMatrixTabHiddenOnColdStart() {
  hiddenAt = null;
  resumeConsumedForCycle = false;
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(MATRIX_TAB_HIDDEN_AT_KEY);
  } catch (_) {
    // ignore
  }
}

function consumeTabResume(source) {
  if (resumeConsumedForCycle) return null;
  resumeConsumedForCycle = true;

  const storedHiddenAt = readStorage(MATRIX_TAB_HIDDEN_AT_KEY, { remove: true });
  let started = hiddenAt;
  if (storedHiddenAt != null) {
    started = started != null ? Math.min(started, storedHiddenAt) : storedHiddenAt;
  }
  hiddenAt = null;

  const hiddenMs =
    started != null && Number.isFinite(started) ? Math.max(0, Date.now() - started) : 0;

  return {
    hiddenMs,
    resumeFreshInitialSync: needsFreshInitialSync(hiddenMs),
    longIdle: hiddenMs >= TAB_VISIBLE_FORCE_RECOVERY_AFTER_HIDDEN_MS || isMatrixSyncStale(),
    source,
  };
}

function notifyTabResume(source) {
  const payload = consumeTabResume(source);
  if (!payload) return;
  for (const listener of resumeListeners) {
    try {
      listener(payload);
    } catch (_) {
      // ignore subscriber errors
    }
  }
}

function ensureCoordinatorAttached() {
  if (coordinatorAttached || typeof document === 'undefined') return;
  coordinatorAttached = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      recordTabHidden();
    } else if (document.visibilityState === 'visible') {
      notifyTabResume('visibilitychange');
    }
  });

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', recordTabHidden);
    window.addEventListener('pageshow', (e) => {
      if (e?.persisted) notifyTabResume('pageshow-bfcache');
    });
  }
}

/**
 * @param {(payload: MatrixTabResumePayload) => void} listener
 * @returns {() => void}
 */
export function subscribeMatrixTabResume(listener) {
  if (typeof listener !== 'function') return () => {};
  ensureCoordinatorAttached();
  resumeListeners.add(listener);
  return () => {
    resumeListeners.delete(listener);
  };
}
